import type { Command } from './useSlashMenu';

import React, { memo, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { Range } from 'slate';
import { useSlateStatic, ReactEditor } from 'slate-react';

import MenuDropdown from '@ui/MarkdownEditor/editor/MenuDropdown';
import { MenuActionTypes } from '@ui/MarkdownEditor/enums';
import useClassName from '@ui/MarkdownEditor/useClassName';
import Menu from '@ui/Menu';

import { transforms } from './commands';
import SlashMenuItems from './SlashMenuItems';
import SlashMenuTooltip from './SlashMenuTooltip';
import classes from './style.module.scss';
import useSlashMenu from './useSlashMenu';

type CommandByCategory = [category: Command['category'], command: Command];

const chunk = (list: Command[]): CommandByCategory[] => {
  return list.reduce((acc, item) => {
    const categoryChunk = acc.find(([category]) => item.category === category);

    if (!categoryChunk) {
      acc.push([item.category, item]);
    } else {
      categoryChunk.push(item);
    }

    return acc;
  }, [] as CommandByCategory[]);
};

const SlashMenu = () => {
  const editor = useSlateStatic();
  const [{ selected, filtered, rangeRef, target, open }, dispatch] = useSlashMenu();
  const selectedName = filtered[selected]?.name;
  const results = useMemo(() => chunk(filtered), [filtered]);
  const menuRef = useRef<HTMLElement>(null);

  const [hover, setHover] = useState(0);

  const onHover = useCallback(() => setHover(hover + 1), [hover, setHover]);

  const onClick = useCallback(
    (_, name) => {
      ReactEditor.focus(editor);
      transforms[name](editor);

      dispatch({ type: MenuActionTypes.close });
    },
    [dispatch, editor],
  );

  const close = useCallback(() => dispatch({ type: MenuActionTypes.close }), [dispatch]);

  const isVisible = Boolean(menuRef.current && open);

  useEffect(() => {
    if (!isVisible || !menuRef.current) return;

    menuRef.current.scrollTop = 0;
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !menuRef.current) return;

    const menuItem = ([...menuRef.current.children] as HTMLElement[]).filter(item => item.dataset.name)[selected];
    if (!menuItem) return;
    // @note: not available in jsdom?!
    if (!menuItem.scrollIntoView) {
      return;
    }

    menuItem.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [selected, isVisible]);

  useEffect(() => {
    if (rangeRef?.current && Range.isCollapsed(rangeRef.current)) {
      dispatch({ type: MenuActionTypes.close });
    }
    // @note: rangeRef is mutable, so we really need to be checking against rangeRef.current for selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, rangeRef?.current]);

  const className = useClassName(
    classes['SlashMenu-Menu'],
    filtered.length > 0 && classes['SlashMenu-Menu-results'],
    filtered.length === 1 && classes['SlashMenu-Menu-one_result'],
  );

  return (
    <MenuDropdown className={`${classes.SlashMenu} SlashMenu`} close={close} open={open} target={target}>
      <Menu ref={menuRef} className={className} onMouseOver={onHover} role="menu">
        <SlashMenuItems onClick={onClick} results={results} selectedName={selectedName} />
      </Menu>
      {!editor.props.basic && <SlashMenuTooltip menuRef={menuRef} />}
    </MenuDropdown>
  );
};

export default memo(SlashMenu);
